<template>
    <div>
        <div class="editor" :class="[hideBorder ? 'editor__hide-border' : '']">
            <editor-menu-bar :editor="editor" v-slot="{ commands, isActive }" v-if="!readonly">
                <div class="menubar">
                    <button
                        class="menubar__button"
                        :class="{ 'is-active': isActive.bold() }"
                        @click="commands.bold"
                    >
                        <core-icon
                            height="18"
                            class="pointer"
                            iconName="Bold text"
                        >
                            <icons-bold-text></icons-bold-text>
                        </core-icon>
                    </button>

                    <button
                        class="menubar__button"
                        :class="{ 'is-active': isActive.italic() }"
                        @click="commands.italic"
                    >
                        <core-icon
                            height="18"
                            class="pointer"
                            iconName="Italic text"
                        >
                            <icons-italic-text></icons-italic-text>
                        </core-icon>
                    </button>

                    <button
                        class="menubar__button"
                        :class="{ 'is-active': isActive.strike() }"
                        @click="commands.strike"
                    >
                        <core-icon
                            height="18"
                            class="pointer"
                            iconName="Strike through text"
                        >
                            <icons-strike-text></icons-strike-text>
                        </core-icon>
                    </button>

                    <button
                        class="menubar__button"
                        :class="{ 'is-active': isActive.underline() }"
                        @click="commands.underline"
                    >
                        <core-icon
                            height="18"
                            class="pointer"
                            iconName="Underline text"
                        >
                            <icons-underline-text></icons-underline-text>
                        </core-icon>
                    </button>

                    <button
                        class="menubar__button"
                        :class="{ 'is-active': isActive.paragraph() }"
                        @click="commands.paragraph"
                    >
                        <core-icon
                            height="18"
                            class="pointer"
                            iconName="Paragraph"
                        >
                            <icons-paragraph></icons-paragraph>
                        </core-icon>
                    </button>

                    <button
                        class="menubar__button"
                        :class="{ 'is-active': isActive.heading({ level: 1 }) }"
                        @click="commands.heading({ level: 1 })"
                    >
                        <core-icon
                            height="18"
                            class="pointer"
                            iconName="H1"
                        >
                            <icons-h1></icons-h1>
                        </core-icon>
                    </button>

                    <button
                        class="menubar__button"
                        :class="{ 'is-active': isActive.heading({ level: 2 }) }"
                        @click="commands.heading({ level: 2 })"
                    >
                        <core-icon
                            height="18"
                            class="pointer"
                            iconName="H2"
                        >
                            <icons-h2></icons-h2>
                        </core-icon>
                    </button>

                    <button
                        class="menubar__button"
                        :class="{ 'is-active': isActive.heading({ level: 3 }) }"
                        @click="commands.heading({ level: 3 })"
                    >
                        <core-icon
                            height="18"
                            class="pointer"
                            iconName="H3"
                        >
                            <icons-h3></icons-h3>
                        </core-icon>
                    </button>

                    <button
                        class="menubar__button"
                        :class="{ 'is-active': isActive.bullet_list() }"
                        @click="commands.bullet_list"
                    >
                        <core-icon
                              height="18"
                              class="pointer"
                              iconName="Unordered list"
                          >
                              <icons-unordered-list></icons-unordered-list>
                          </core-icon>
                    </button>

                    <button
                        class="menubar__button"
                        :class="{ 'is-active': isActive.ordered_list() }"
                        @click="commands.ordered_list"
                    >
                        <core-icon
                            height="18"
                            class="pointer"
                            iconName="Ordered list"
                        >
                            <icons-ordered-list></icons-ordered-list>
                        </core-icon>
                    </button>

                    <button
                        class="menubar__button"
                        @click="commands.horizontal_rule"
                    >
                        <core-icon
                            height="18"
                            class="pointer"
                            iconName="Horizontal rule"
                        >
                            <icons-horizontal-rule></icons-horizontal-rule>
                        </core-icon>
                    </button>

                    <button
                        class="menubar__button"
                        @click="commands.undo"
                    >
                        <core-icon
                            height="18"
                            class="pointer"
                            iconName="Undo"
                        >
                            <icons-undo></icons-undo>
                        </core-icon>
                    </button>

                    <button
                        class="menubar__button"
                        @click="commands.redo"
                    >
                        <core-icon
                            height="18"
                            class="pointer"
                            iconName="Redo"
                        >
                            <icons-redo></icons-redo>
                        </core-icon>
                    </button>
                    <button v-if="0"
                        class="menubar__button"
                        @click="addImage"
                    >
                        <core-icon
                            height="18"
                            class="pointer"
                            iconName="Image"
                        >
                            <icons-image></icons-image>
                        </core-icon>
                    </button>
                </div>
            </editor-menu-bar>
            <editor-content @paste="handlePaste" :editor="editor" class="editor-content"></editor-content>
        </div>
    </div>
</template>

<script>
import { Editor, EditorContent, EditorMenuBar } from 'tiptap'
import {
  HardBreak,
  Heading,
  HorizontalRule,
  OrderedList,
  BulletList,
  ListItem,
  TodoItem,
  TodoList,
  Bold,
  Italic,
  Link,
  Strike,
  Underline,
  History,
  Image
} from 'tiptap-extensions'
export default {
  props: {
    value: {
      type: String,
      required: false,
      default: ''
    },
    readonly: {
      type: Boolean,
      required: false,
      default: false
    },
    hideBorder: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  data () {
    return {
      editor: null
    }
  },
  components: {
    EditorContent,
    EditorMenuBar
  },
  created () {
    this.editor = new Editor({
      editable: !this.readonly,
      extensions: [
        new BulletList(),
        new HardBreak(),
        new Heading({ levels: [1, 2, 3] }),
        new HorizontalRule(),
        new ListItem(),
        new OrderedList(),
        new TodoItem(),
        new TodoList(),
        new Link(),
        new Bold(),
        new Italic(),
        new Strike(),
        new Underline(),
        new History(),
        new Image({inline: true, allowBase64: true})
      ],
      onUpdate: ({ getHTML }) => {
        this.$emit('input', getHTML())
      },
      editorProps: {
        handleDOMEvents: {
          paste: (view, event) => {
            return this.handlePaste(event)
          }
        }
      }
    })
    this.editor.setContent(this.value)
  },
  beforeDestroy () {
    if (this.editor) {
      this.editor.destroy()
    }
  },
  methods: {
    reset () {
      if (this.editor) {
        this.editor.clearContent(true)
      }
    },
    getBase64 (file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = () => resolve(reader.result)
        reader.onerror = error => reject(error)
        reader.readAsDataURL(file)
      })
    },
    async handlePaste (event) {
      const clipboardItems = event.clipboardData.items
      for (const item of clipboardItems) {
        if (item.type.startsWith('image/')) {
          event.preventDefault()
          event.stopPropagation()
          const file = item.getAsFile()
          const base64 = await this.getBase64(file)
          this.insertImage(base64)
          return false
        }
      }
      return true
    },
    insertImage (base64) {
      // this.editor.setContent(`<img src="${base64}">`)
      this.editor.commands.image({ src: base64, contenteditable: true })
    },
    async addImage (event) {
      if (event && event.clipboardData) {
        const clipboardItems = event.clipboardData.items
        for (const item of clipboardItems) {
          if (item.type.startsWith('image/')) {
            const file = item.getAsFile()
            const base64 = await this.getBase64(file)
            this.insertImage(base64)
            return
          }
        }
      }
      const url = window.prompt('URL')
      if (url) {
        this.editor.commands.image({ src: url })
      }
    }
  },
  watch: {
    value (val) {
      // so cursor doesn't jump to start on typing
      // courtesy of StackOverflow: https://stackoverflow.com/a/65700241/7957212
      if (this.editor) {
        let html = this.editor.getHTML()
        if (html !== val) {
          this.editor.setContent(val)
        }
      }
    }
  }
}
</script>

<style>
.editor-content img {
  max-width: 100%;
}
</style>



// WEBPACK FOOTER //
// src/components/core/Editor.vue